import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Star, MapPin, BadgeCheck, Clock } from "lucide-react";

const HERO_IMG = "/images/img4.jpg";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

const MaskedLine = ({ index, children, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span className="block will-change-transform" {...lineAnim(index)}>
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { icon: BadgeCheck, label: "Consultanță după seria de șasiu (VIN)" },
  { icon: MapPin, label: "Stoc local · Dragalina 27" },
  { icon: MessageCircle, label: "WhatsApp · 0786 804 728" },
  { icon: Phone, label: "Piese și pe comandă" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section id="acasa" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden hero-grid-bg pt-[72px]">
      <div className="absolute inset-0 speed-lines" />
      <div className="absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-[#0055FF]/15 blur-[140px]" />
      <div className="absolute bottom-0 -left-40 h-[420px] w-[420px] rounded-full bg-[#FF2A3B]/10 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-end pb-10 pt-16 lg:pt-24">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-12 bg-[#FF2A3B]" />
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-slate-400">
              Magazin de piese auto · Est. Timișoara
            </span>
          </motion.div>

          <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-[11vw] sm:text-6xl lg:text-7xl xl:text-[86px] pr-2">
            <MaskedLine index={0}>Piese auto</MaskedLine>
            <MaskedLine index={1}>
              <span className="text-[#0055FF]">de performanță,</span>
            </MaskedLine>
            <MaskedLine index={2}>
              servire <span className="text-stroke-red">rapidă.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            SC Speed Shop Power SRL — magazinul tău de piese auto pe{" "}
            <span className="text-white">Bulevardul General Ion Dragalina 27</span>, Timișoara.
            Stoc real, consultanță după seria de șasiu (VIN), iar piesele care nu sunt pe stoc le aducem pe comandă.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-call-cta"
              href="tel:+40786804728"
              className="group flex items-center gap-3 bg-[#FF2A3B] hover:bg-[#0055FF] px-7 py-4 font-display font-bold uppercase tracking-wide text-sm transition-colors duration-300"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              Sună pentru oferte
            </a>
            <button
              data-testid="hero-whatsapp-cta"
              onClick={() => window.__lenis?.scrollTo("#whatsapp", { offset: -72, duration: 1.4 })}
              className="group flex items-center gap-3 border border-white/20 hover:border-[#0055FF] px-7 py-4 font-display font-bold uppercase tracking-wide text-sm text-slate-200 hover:text-white transition-colors duration-300"
            >
              <MessageCircle className="h-4 w-4 text-[#0055FF]" />
              Cere piesa pe WhatsApp
            </button>
            <div data-testid="hero-rating-pill" className="flex items-center gap-2 border border-white/10 bg-[#131927]/80 px-4 py-3">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-semibold">3,5 / 5</span>
              <span className="text-xs text-slate-500">· 14 recenzii Google</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.3, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative hidden lg:block"
        >
          <div className="relative h-[520px] overflow-hidden border border-white/10">
            <motion.img
              src={HERO_IMG}
              alt="Mașină roșie demontată cu toate piesele așezate în jurul ei"
              style={{ y: imgY, scale: 1.25 }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07122B] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-300">
                Fiecare piesă, la locul ei
              </span>
              <span className="h-2 w-2 bg-[#FF2A3B]" />
            </div>
          </div>
          <div className="absolute -top-3 -left-3 h-16 w-16 border-t-2 border-l-2 border-[#0055FF]" />
          <div className="absolute -bottom-3 -right-3 h-16 w-16 border-b-2 border-r-2 border-[#FF2A3B]" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="lg:hidden relative h-52 overflow-hidden border border-white/10"
        >
          <img
            src={HERO_IMG}
            alt="Mașină roșie demontată cu toate piesele așezate în jurul ei"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07122B]/70 via-transparent to-transparent" />
          <span className="absolute bottom-3 left-3 text-[10px] font-mono uppercase tracking-[0.3em] text-slate-300">
            Fiecare piesă, la locul ei
          </span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.25 }}
        className="relative border-t border-white/10 bg-[#07122B]/70 backdrop-blur-sm"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              data-testid={`hero-stat-${i}`}
              className="flex items-center gap-3 py-5 px-2 lg:px-6 border-l border-white/5 first:border-l-0"
            >
              <s.icon className="h-5 w-5 text-[#0055FF] shrink-0" />
              <span className="text-xs sm:text-sm text-slate-300 font-medium">{s.label}</span>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute top-24 right-6 hidden xl:flex items-center gap-2 text-slate-500"
      >
        <Clock className="h-3.5 w-3.5" />
        <span className="text-[10px] font-mono uppercase tracking-[0.3em]">Timișoara · RO</span>
      </motion.div>
    </section>
  );
}
